/** A person on the family roster who may exhibit at a show. */

/**
 * A member as the roster and its drawer read it: the record's own columns plus
 * every dynamic `formData` key, named by the `fieldKey` the DynamicForm API
 * returns. Only what the roster itself renders is typed — the rest of the
 * form's values ride the index signature, as `FamilyProfileResponse` does.
 */
export interface FamilyMember {
  /** `familyMemberUuid` — the row's identity. */
  memberId: string;
  /** Whole years from the form's `dob`, for the card's meta line. */
  age?: number | null;
  [fieldKey: string]: unknown;
}

/** A dynamic value read as text. Nothing the *form* owns is declared on
 *  `FamilyMember` — the configuration decides which fields exist — so readers
 *  coerce at the point of use instead of re-declaring the field list. */
export function memberText(member: FamilyMember, key: string): string | null {
  const value = member[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

/** First non-blank string among the candidates, or null. A blank string (from
 *  either source) doesn't count — an empty column must not be read as "on
 *  file" over a formData value that actually holds something. */
function firstNonBlank(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return null;
}

/** One show a member is mapped to. `showId` is the show's GUID, not its
 *  `sa` / `hdgs` route code. */
export interface FamilyMemberShowMappingRow {
  familyMemberShowMappingUuid?: string;
  showId?: string | null;
  isActive?: boolean | null;
}

/** A FamilyMember row exactly as GetAllFamilyMembers and UpsertFamilyMembers
 *  return it: own columns, plus every dynamic field as a JSON string. */
export interface FamilyMemberRecord {
  id: number;
  familyMemberUuid: string;
  familyUuid?: string | null;
  educatorUuid?: string | null;
  userType?: string | null;
  isActive?: boolean;
  email?: string | null;
  formData?: string | null;
  /** Total number of entries across all shows. */
  totalEntryCount?: number;
  /** Clubs those entries were filed under — the 4-H / FFA values that can't change. */
  entryClubUuids?: string[];
  /** Every show this member is registered for — one row per show, so a member
   *  entered in San Antonio and Hill District carries two. */
  showMappings?: FamilyMemberShowMappingRow[];
}

/**
 * Whether a member is already registered for one particular show.
 *
 * A member carries a mapping per show, so the *presence* of mappings says
 * nothing on its own — only a mapping naming this show does. `showUuid` is the
 * show's GUID; null means it couldn't be resolved, which reads as "not
 * registered" so the picker offers the member rather than hiding everyone.
 */
export function isRegisteredForShow(
  mappings: FamilyMemberShowMappingRow[] | null | undefined,
  showUuid: string | null | undefined
): boolean {
  if (!showUuid) {
    return false;
  }
  return (mappings ?? []).some(
    (mapping) =>
      mapping?.isActive !== false &&
      typeof mapping?.showId === 'string' &&
      mapping.showId.toLowerCase() === showUuid.toLowerCase()
  );
}

/** A stored club as its dropdown's value. Options carry lowercased uuids but
 *  saved rows can hold uppercase ones, which would render blank. */
export function clubOptionValue(value: unknown): string {
  if (typeof value !== 'string' || !value.trim() || value === 'N/A') {
    return 'N/A';
  }
  return value.trim().toLowerCase();
}

/** Whether a club dropdown's value is one the member's entries were filed under. */
export function isEntryClub(entryClubUuids: unknown, clubUuid: unknown): boolean {
  if (typeof clubUuid !== 'string' || !Array.isArray(entryClubUuids)) {
    return false;
  }
  const uuid = clubUuid.toLowerCase();
  return entryClubUuids.some((c) => typeof c === 'string' && c.toLowerCase() === uuid);
}

/** Whole-year age from a date of birth. */
export function ageFromDob(dob: string | Date | null | undefined): number | null {
  if (!dob) {
    return null;
  }
  const birth = dob instanceof Date ? dob : new Date(dob);
  if (Number.isNaN(birth.getTime())) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const months = today.getMonth() - birth.getMonth();
  if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Flattens a member row — its own columns plus the dynamic fields buried in the
 * `formData` JSON string — into the bag the roster works with.
 *
 * The API stores the upsert's raw request body as `formData`, so its keys are
 * the form's own fieldKeys and the result patches straight back into the
 * dynamic form without a translation step.
 */
export function toFamilyMember(row: FamilyMemberRecord): FamilyMember {
  const { formData: encoded, ...columns } = row;

  let formData: Record<string, unknown> = {};
  if (encoded) {
    try {
      formData = JSON.parse(encoded);
    } catch (e) {
      console.error('[family-member] Failed to parse formData:', e);
    }
  }

  // Rows written before the client sent the form's own fieldKeys carry the old
  // Exhibitor names, so both spellings are read. New saves only ever write the
  // fieldKey, which is also the only name the API parses back out.
  const dob = (formData['dob'] ?? formData['dateOfBirth'] ?? null) as string | null;
  const mobile = (formData['mobile'] ?? formData['mobilePhone'] ?? null) as string | null;
  // The column stands in when the form never captured an address.
  const email = (formData['email'] ?? row.email ?? null) as string | null;
  // The household pair can live in formData, in its own row columns, or (on
  // stale rows) only one of the two — a blank in either place must not hide a
  // real value held in the other.
  const columnBag = columns as Record<string, unknown>;
  const familyName = firstNonBlank(formData['familyName'], columnBag['familyName']);
  const familyEmail = firstNonBlank(formData['familyEmail'], columnBag['familyEmail']);

  // The record's own columns ride along too, so readers that key off
  // `familyMemberUuid` / `familyUuid` still find them on the flattened member.
  // A null column never erases a value the form captured: GetAllExhibitor
  // returns `qualityCounts: null` beside a formData that holds the number.
  const merged: Record<string, unknown> = { ...formData };
  for (const [key, value] of Object.entries(columns)) {
    if (value != null || merged[key] === undefined) {
      merged[key] = value;
    }
  }

  return {
    ...merged,
    memberId: row.familyMemberUuid,
    email,
    familyName,
    familyEmail,
    dob,
    mobile,
    age: ageFromDob(dob)
  };
}
